import {Controller} from "stimulus"

/**
 * Stimulus controller for the savings milestone graph.
 * Measures the chart area height and calculates the blue progress bar height dynamically.
 *
 * Uses Stimulus 1.x syntax (data-target="savings-milestone.bar").
 *
 * Data attributes on controller element:
 * - data-current-bar-position: Float (0=bottom, 4=top) - how many milestones achieved from bottom
 * - data-num-milestones: Number of milestone rows (default 4)
 * - data-row-height: Height of each row in px (default 20)
 * - data-row-gap: Gap between rows in px (default 8)
 */
export default class extends Controller {
  static get targets() {
    return ["chartArea", "bar"]
  }

  connect() {
    // Wait a tick to ensure DOM is ready
    setTimeout(() => this.updateBarHeight(), 0)
    // Update on resize
    this.resizeObserver = new ResizeObserver(() => this.updateBarHeight())
    if (this.hasChartAreaTarget) {
      this.resizeObserver.observe(this.chartAreaTarget)
    }
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  /**
   * Read data attributes from the controller element (Stimulus 1.x style)
   */
  getData(name, defaultValue) {
    const value = this.element.dataset[name] || this.data.get(name)
    return value !== undefined && value !== null ? value : defaultValue
  }

  updateBarHeight() {
    if (!this.hasChartAreaTarget || !this.hasBarTarget) {
      // Retry if targets not ready
      if (!this._retryCount) this._retryCount = 0
      if (this._retryCount < 10) {
        this._retryCount++
        setTimeout(() => this.updateBarHeight(), 100)
      }
      return
    }

    const chartHeight = this.chartAreaTarget.offsetHeight
    if (chartHeight === 0) {
      // Try again after a short delay if not yet measured
      if (!this._heightRetryCount) this._heightRetryCount = 0
      if (this._heightRetryCount < 20) {
        this._heightRetryCount++
        setTimeout(() => this.updateBarHeight(), 50)
      }
      return
    }

    // Reset retry counters on success
    this._retryCount = 0
    this._heightRetryCount = 0

    // Read configuration from data attributes (camelCase for dataset access)
    const currentBarPosition = parseFloat(this.getData("currentBarPosition", 0))
    const numMilestones = parseInt(this.getData("numMilestones", 4), 10)
    const rowHeight = parseFloat(this.getData("rowHeight", 20))

    // Calculate bar height to align with milestones (matching React component logic)
    // currentBarPosition represents how many milestones achieved from bottom up:
    // 0 = no bar visible
    // 1 = bar reaches bottom milestone (e.g., $1,500 hardship)
    // 2 = bar reaches 2nd from bottom (e.g., 3+ months salary)
    // 3 = bar reaches 3rd from bottom (e.g., 6+ months salary)
    // 4 = bar reaches top milestone (e.g., 1+ year salary)

    // milestoneSpacing = distance between row tops = (totalHeight - oneRowHeight) / (numRows - 1)
    const milestoneSpacing = chartHeight > 0 && numMilestones > 1 ? (chartHeight - rowHeight) / (numMilestones - 1) : 0

    // Bar height calculation:
    // The bar base aligns with the bottom of the lowest label text. The first
    // milestone line sits at half the row height above that base, then each
    // additional milestone is one spacing step above.
    // - Position 1: rowHeight / 2 (bottom milestone line)
    // - Position 2: rowHeight / 2 + spacing
    // - Position N: rowHeight / 2 + (N-1) * spacing
    const firstMilestoneOffset = rowHeight / 2
    const maxBarHeight = firstMilestoneOffset + (numMilestones - 1) * milestoneSpacing
    const calculatedHeight = firstMilestoneOffset + (currentBarPosition - 1) * milestoneSpacing
    const barHeight = chartHeight > 0 && currentBarPosition > 0 ? Math.min(calculatedHeight, maxBarHeight) : 0

    // Set bar height via inline style
    if (barHeight > 0) {
      this.barTarget.style.height = `${barHeight}px`
      this.barTarget.style.display = "block"
    } else {
      this.barTarget.style.display = "none"
    }
  }
}
